import { Link } from 'react-router-dom';
import type { CSSProperties, ReactNode, MouseEventHandler } from 'react';

interface CardLinkProps {
    /** Destination route. */
    to: string;
    /** Accessible name for the link (the visible content is decorative to the overlay). */
    ariaLabel?: string;
    className?: string;
    style?: CSSProperties;
    children: ReactNode;
    onMouseEnter?: MouseEventHandler<HTMLDivElement>;
    onMouseLeave?: MouseEventHandler<HTMLDivElement>;
}

/**
 * CardLink — turns a whole card/row into a real navigable link so ⌘-click,
 * middle-click ("open in new tab"), right-click, and keyboard focus/Enter all
 * work. A plain `<div onClick={() => navigate(...)}>` supports none of those and
 * is invisible to assistive tech.
 *
 * Uses the "stretched link" pattern: an absolutely-positioned `<Link>` overlay
 * covers the card and carries the navigation, while the visible content paints
 * normally underneath it. Any nested interactive element (a ⋯ menu, a delete
 * button) stays clickable as long as it sits above the overlay — give it
 * `position` + `z-index >= 2` (most already do). Non-interactive content needs
 * no changes: clicks on it fall through to the overlay and navigate.
 */
export function CardLink({ to, ariaLabel, className, style, children, onMouseEnter, onMouseLeave }: CardLinkProps) {
    return (
        <div
            className={className}
            style={{ position: 'relative', ...style }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <Link to={to} aria-label={ariaLabel} className="card-link-overlay" />
            {children}
        </div>
    );
}
